import { apiClient, requestJson, unwrap } from './client.js';
import type { PathBody, PathHeaders, PathQuery } from './types.js';

export const listProviderConnections = (query: ProviderConnectionQuery = {}) =>
  unwrap(apiClient.GET('/v1/provider-connections', { params: { query } }));
export type ProviderConnectionQuery = PathQuery<'/v1/provider-connections', 'get'>;
export type ProviderConnectionPage = Awaited<ReturnType<typeof listProviderConnections>>;
export type ProviderConnection = ProviderConnectionPage['items'][number];

export const listManagedLibraries = (query: ManagedLibraryQuery = {}) =>
  unwrap(apiClient.GET('/v1/libraries', { params: { query } }));
export type ManagedLibraryQuery = PathQuery<'/v1/libraries', 'get'>;
export type ManagedLibraryPage = Awaited<ReturnType<typeof listManagedLibraries>>;
export type ManagedLibrary = ManagedLibraryPage['items'][number];

export const listDestinations = (query: DestinationQuery = {}) =>
  unwrap(apiClient.GET('/v1/destinations', { params: { query } }));
export type DestinationQuery = PathQuery<'/v1/destinations', 'get'>;
export type DestinationPage = Awaited<ReturnType<typeof listDestinations>>;
export type Destination = DestinationPage['items'][number];

export const getCreationCapabilities = (connectionId: string) =>
  unwrap(
    apiClient.GET('/v1/creation-capabilities/{connectionId}', {
      params: { path: { connectionId } },
    }),
  );
export type CreationCapabilities = Awaited<ReturnType<typeof getCreationCapabilities>>;

export type CreationStyleTagValidationRequest = PathBody<
  '/v1/creation-style-tags/{connectionId}/validations',
  'post'
>;
export const validateCreationStyleTag = (
  connectionId: string,
  request: CreationStyleTagValidationRequest,
) =>
  unwrap(
    apiClient.POST('/v1/creation-style-tags/{connectionId}/validations', {
      params: { path: { connectionId } },
      body: request,
    }),
  );
export type CreationStyleTagValidation = Awaited<ReturnType<typeof validateCreationStyleTag>>;

export const listCreationPresets = (connectionId: string) =>
  unwrap(
    apiClient.GET('/v1/creation-presets', {
      params: { query: { connection_id: connectionId } },
    }),
  );
export type CreationPresetPage = Awaited<ReturnType<typeof listCreationPresets>>;
export type CreationPreset = CreationPresetPage['items'][number];

export type CreateCreationPresetBody = PathBody<'/v1/creation-presets', 'post'>;
export const createCreationPreset = (request: CreateCreationPresetBody, idempotencyKey: string) =>
  unwrap(
    apiClient.POST('/v1/creation-presets', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body: request,
    }),
  );

export type CreatePresetGenerationBody = PathBody<
  '/v1/creation-presets/{presetId}/generations',
  'post'
>;
export const createPresetGeneration = (
  presetId: string,
  request: CreatePresetGenerationBody,
  idempotencyKey: string,
) =>
  unwrap(
    apiClient.POST('/v1/creation-presets/{presetId}/generations', {
      params: { path: { presetId }, header: { 'Idempotency-Key': idempotencyKey } },
      body: request,
    }),
  );

export type CreateGenerationBody = PathBody<'/v1/generations', 'post'>;
export const createGeneration = (request: CreateGenerationBody, idempotencyKey: string) =>
  unwrap(
    apiClient.POST('/v1/generations', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body: request,
    }),
  );
export type Generation = Awaited<ReturnType<typeof createGeneration>>;

export type GenerationQuery = PathQuery<'/v1/generations', 'get'>;
export const listGenerations = (query: GenerationQuery = {}) =>
  unwrap(apiClient.GET('/v1/generations', { params: { query } }));
export type GenerationPage = Awaited<ReturnType<typeof listGenerations>>;

export type ProjectQuery = PathQuery<'/v1/projects', 'get'>;
export const listProjects = (query: ProjectQuery = {}) =>
  unwrap(apiClient.GET('/v1/projects', { params: { query } }));
export type ProjectPage = Awaited<ReturnType<typeof listProjects>>;
export type Project = ProjectPage['items'][number];

export type ProjectCardQuery = PathQuery<'/v1/project-cards', 'get'>;
export const listProjectCards = (query: ProjectCardQuery) =>
  unwrap(apiClient.GET('/v1/project-cards', { params: { query } }));
export type ProjectCardPage = Awaited<ReturnType<typeof listProjectCards>>;
export type ProjectCard = ProjectCardPage['items'][number];

export type AllProjectCardsQuery = Omit<ProjectCardQuery, 'cursor'>;
export async function listAllProjectCards(query: AllProjectCardsQuery): Promise<ProjectCardPage> {
  const items: ProjectCard[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await listProjectCards({ ...query, ...(cursor ? { cursor } : {}) });
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
    if (cursor && seenCursors.has(cursor)) {
      throw new Error('Project card pagination repeated a cursor.');
    }
    if (cursor) seenCursors.add(cursor);
  } while (cursor);
  return { items, next_cursor: null };
}

export type AllProjectsQuery = Omit<ProjectQuery, 'cursor'>;
export async function listAllProjects(query: AllProjectsQuery = {}): Promise<ProjectPage> {
  const items: Project[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await listProjects({ ...query, ...(cursor ? { cursor } : {}) });
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
    if (cursor && seenCursors.has(cursor)) throw new Error('Project pagination repeated a cursor.');
    if (cursor) seenCursors.add(cursor);
  } while (cursor);
  return { items, next_cursor: null };
}

export const getProject = (projectId: string) =>
  unwrap(
    apiClient.GET('/v1/projects/{projectId}', {
      params: { path: { projectId } },
    }),
  );

export type OpeningImpactAuditStatus =
  'queued' | 'running' | 'succeeded' | 'partially_failed' | 'failed';

export type OpeningImpactAudit = {
  id: string;
  composer_project_id: string;
  provider_connection_id: string;
  managed_library_id: string;
  detector_version: 'kk7-frozen-boundary-v1';
  threshold: number;
  status: OpeningImpactAuditStatus;
  counts: {
    total: number;
    pending: number;
    analyzed: number;
    failed: number;
    violates: number;
    does_not_violate: number;
  };
  results: Array<{
    track_id: string;
    status: 'pending' | 'succeeded' | 'failed';
    score: number | null;
    violates: boolean | null;
    failure: {
      code: string;
      message: string;
      retryable: boolean;
      action_required: string | null;
    } | null;
    analyzed_at: string | null;
  }>;
  failure: {
    code: string;
    message: string;
    retryable: boolean;
    action_required: string | null;
  } | null;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export const getOpeningImpactAudit = (projectId: string, connectionId: string, libraryId: string) =>
  requestJson<{ audit: OpeningImpactAudit | null }>(
    `/v1/projects/${encodeURIComponent(projectId)}/opening-impact-audit?connection_id=${encodeURIComponent(connectionId)}&library_id=${encodeURIComponent(libraryId)}`,
  );

export const requestOpeningImpactAudit = (
  projectId: string,
  connectionId: string,
  libraryId: string,
  idempotencyKey: string,
) =>
  requestJson<OpeningImpactAudit>(
    `/v1/projects/${encodeURIComponent(projectId)}/opening-impact-audits`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ connection_id: connectionId, library_id: libraryId }),
    },
  );

export type CreateProjectBody = PathBody<'/v1/projects', 'post'>;
export const createProject = (request: CreateProjectBody, idempotencyKey: string) =>
  unwrap(
    apiClient.POST('/v1/projects', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body: request,
    }),
  );

export type UpdateProjectBody = PathBody<'/v1/projects/{projectId}', 'put'>;
export const updateProject = (projectId: string, request: UpdateProjectBody) =>
  unwrap(
    apiClient.PUT('/v1/projects/{projectId}', {
      params: { path: { projectId } },
      body: request,
    }),
  );

export const deleteProject = (projectId: string, expectedRevision: number) =>
  unwrap(
    apiClient.DELETE('/v1/projects/{projectId}', {
      params: {
        path: { projectId },
        query: { expected_revision: expectedRevision },
      },
    }),
  );

export type GenerationViewQuery = PathQuery<'/v1/generation-views/{generationId}', 'get'>;
export const getGenerationView = (generationId: string, query: GenerationViewQuery) =>
  unwrap(
    apiClient.GET('/v1/generation-views/{generationId}', {
      params: { path: { generationId }, query },
    }),
  );
export type GenerationView = Awaited<ReturnType<typeof getGenerationView>>;

export type TrackCardQuery = PathQuery<'/v1/track-cards', 'get'>;
export const listTrackCards = (query: TrackCardQuery) =>
  unwrap(apiClient.GET('/v1/track-cards', { params: { query } }));
export type TrackCardPage = Awaited<ReturnType<typeof listTrackCards>>;
export type TrackCard = TrackCardPage['items'][number];

export type LibraryTrackCardQuery = PathQuery<'/v1/libraries/{libraryId}/track-cards', 'get'>;
export const listLibraryTrackCards = (libraryId: string, query: LibraryTrackCardQuery = {}) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/track-cards', {
      params: { path: { libraryId }, query },
    }),
  );
export type LibraryTrackCardPage = Awaited<ReturnType<typeof listLibraryTrackCards>>;

export type TrackDetailQuery = PathQuery<'/v1/track-details/{trackId}', 'get'>;
export const getTrackDetail = (trackId: string, query: TrackDetailQuery) =>
  unwrap(
    apiClient.GET('/v1/track-details/{trackId}', {
      params: { path: { trackId }, query },
    }),
  );
export type TrackDetail = Awaited<ReturnType<typeof getTrackDetail>>;

export const createPreviewSession = (trackId: string) =>
  unwrap(
    apiClient.POST('/v1/catalog/tracks/{trackId}/preview-sessions', {
      params: { path: { trackId } },
    }),
  );
export type PlaybackSession = Awaited<ReturnType<typeof createPreviewSession>>;

export const createOwnedPlaybackSession = (libraryId: string, importId: string) =>
  unwrap(
    apiClient.POST('/v1/libraries/{libraryId}/imports/{importId}/playback-sessions', {
      params: { path: { libraryId, importId } },
    }),
  );

export type FavoriteBody = PathBody<'/v1/catalog/tracks/{trackId}/favorite', 'put'>;
export const setFavorite = (trackId: string, body: FavoriteBody, idempotencyKey: string) =>
  unwrap(
    apiClient.PUT('/v1/catalog/tracks/{trackId}/favorite', {
      params: { path: { trackId }, header: { 'Idempotency-Key': idempotencyKey } },
      body,
    }),
  );
export type FavoriteCommand = Awaited<ReturnType<typeof setFavorite>>;

export const getFavoriteCommand = (commandId: string) =>
  unwrap(
    apiClient.GET('/v1/favorite-commands/{commandId}', {
      params: { path: { commandId } },
    }),
  );

export type RequestCatalogRemovalBody = PathBody<
  '/v1/libraries/{libraryId}/catalog-removal-commands',
  'post'
>;
export const requestCatalogRemoval = (
  libraryId: string,
  body: RequestCatalogRemovalBody,
  idempotencyKey: string,
) =>
  unwrap(
    apiClient.POST('/v1/libraries/{libraryId}/catalog-removal-commands', {
      params: { path: { libraryId }, header: { 'Idempotency-Key': idempotencyKey } },
      body,
    }),
  );
export type CatalogRemovalCommand = Awaited<ReturnType<typeof requestCatalogRemoval>>;

export const getCatalogRemovalCommand = (commandId: string) =>
  unwrap(
    apiClient.GET('/v1/catalog-removal-commands/{commandId}', {
      params: { path: { commandId } },
    }),
  );

export const restoreCatalogTrack = (libraryId: string, trackId: string) =>
  unwrap(
    apiClient.DELETE('/v1/libraries/{libraryId}/catalog-removals/{trackId}', {
      params: { path: { libraryId, trackId } },
    }),
  );

export const ensureLibraryTrack = (libraryId: string, trackId: string) =>
  unwrap(
    apiClient.PUT('/v1/libraries/{libraryId}/tracks/{trackId}', {
      params: { path: { libraryId, trackId } },
    }),
  );
export type LibraryImport = Awaited<ReturnType<typeof ensureLibraryTrack>>;

export const getLibraryImport = (libraryId: string, importId: string) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/imports/{importId}', {
      params: { path: { libraryId, importId } },
    }),
  );

export type PlaylistQuery = PathQuery<'/v1/libraries/{libraryId}/playlists', 'get'>;
export const listPlaylists = (libraryId: string, query: PlaylistQuery = {}) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/playlists', {
      params: { path: { libraryId }, query },
    }),
  );
export type PlaylistPage = Awaited<ReturnType<typeof listPlaylists>>;
export type PlaylistSummary = PlaylistPage['items'][number];

export const getPlaylistView = (libraryId: string, playlistId: string) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/playlist-views/{playlistId}', {
      params: { path: { libraryId, playlistId } },
    }),
  );
export type PlaylistView = Awaited<ReturnType<typeof getPlaylistView>>;

export type CreatePlaylistBody = PathBody<'/v1/libraries/{libraryId}/playlists', 'post'>;
export const createPlaylist = (
  libraryId: string,
  body: CreatePlaylistBody,
  idempotencyKey: string,
) =>
  unwrap(
    apiClient.POST('/v1/libraries/{libraryId}/playlists', {
      params: { path: { libraryId }, header: { 'Idempotency-Key': idempotencyKey } },
      body,
    }),
  );

export type UpdatePlaylistBody = PathBody<
  '/v1/libraries/{libraryId}/playlists/{playlistId}',
  'put'
>;
export const updatePlaylist = (libraryId: string, playlistId: string, body: UpdatePlaylistBody) =>
  unwrap(
    apiClient.PUT('/v1/libraries/{libraryId}/playlists/{playlistId}', {
      params: { path: { libraryId, playlistId } },
      body,
    }),
  );

export const deletePlaylist = (libraryId: string, playlistId: string) =>
  unwrap(
    apiClient.DELETE('/v1/libraries/{libraryId}/playlists/{playlistId}', {
      params: { path: { libraryId, playlistId } },
    }),
  );

export const getPlaylistItems = (libraryId: string, playlistId: string) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/playlists/{playlistId}/items', {
      params: { path: { libraryId, playlistId } },
    }),
  );
export type PlaylistItems = Awaited<ReturnType<typeof getPlaylistItems>>;

export type ReplacePlaylistItemsBody = PathBody<
  '/v1/libraries/{libraryId}/playlists/{playlistId}/items',
  'put'
>;
export const replacePlaylistItems = (
  libraryId: string,
  playlistId: string,
  body: ReplacePlaylistItemsBody,
) =>
  unwrap(
    apiClient.PUT('/v1/libraries/{libraryId}/playlists/{playlistId}/items', {
      params: { path: { libraryId, playlistId } },
      body,
    }),
  );

export type PlaylistOptionsQuery = PathQuery<
  '/v1/libraries/{libraryId}/tracks/{trackId}/playlist-options',
  'get'
>;
export const listPlaylistOptions = (
  libraryId: string,
  trackId: string,
  query: PlaylistOptionsQuery = {},
) =>
  unwrap(
    apiClient.GET('/v1/libraries/{libraryId}/tracks/{trackId}/playlist-options', {
      params: { path: { libraryId, trackId }, query },
    }),
  );

export type MetadataBody = PathBody<'/v1/libraries/{libraryId}/tracks/{trackId}/metadata', 'put'>;
export const replaceTrackMetadata = (libraryId: string, trackId: string, body: MetadataBody) =>
  unwrap(
    apiClient.PUT('/v1/libraries/{libraryId}/tracks/{trackId}/metadata', {
      params: { path: { libraryId, trackId } },
      body,
    }),
  );

export type ActivityQuery = PathQuery<'/v1/activity', 'get'>;
export const listActivity = (query: ActivityQuery = {}) =>
  unwrap(apiClient.GET('/v1/activity', { params: { query } }));
export type ActivityPage = Awaited<ReturnType<typeof listActivity>>;
export type ActivityItem = ActivityPage['items'][number];

export const listSynchronizations = (connectionId: string) =>
  unwrap(
    apiClient.GET('/v1/provider-connections/{connectionId}/synchronizations', {
      params: { path: { connectionId } },
    }),
  );
export type SynchronizationPage = Awaited<ReturnType<typeof listSynchronizations>>;
export type SynchronizationTask = SynchronizationPage['items'][number];

export type SynchronizationBody = PathBody<
  '/v1/provider-connections/{connectionId}/synchronizations',
  'post'
>;
export const requestSynchronization = (
  connectionId: string,
  body: SynchronizationBody,
  idempotencyKey: string,
) =>
  unwrap(
    apiClient.POST('/v1/provider-connections/{connectionId}/synchronizations', {
      params: { path: { connectionId }, header: { 'Idempotency-Key': idempotencyKey } },
      body,
    }),
  );

export type CreateDeliveryBody = PathBody<'/v1/delivery-requests', 'post'>;
export const createDeliveryRequest = (body: CreateDeliveryBody, idempotencyKey: string) =>
  unwrap(
    apiClient.POST('/v1/delivery-requests', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body,
    }),
  );
export type DeliveryRequest = Awaited<ReturnType<typeof createDeliveryRequest>>;

export type DeliveryViewQuery = PathQuery<'/v1/delivery-views/{requestId}', 'get'>;
export const getDeliveryView = (requestId: string, query: DeliveryViewQuery) =>
  unwrap(
    apiClient.GET('/v1/delivery-views/{requestId}', {
      params: { path: { requestId }, query },
    }),
  );
export type DeliveryView = Awaited<ReturnType<typeof getDeliveryView>>;

export type WebSession = Awaited<ReturnType<typeof getWebSession>>;
function parseWebSession(payload: unknown) {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('user' in payload) ||
    typeof payload.user !== 'object' ||
    payload.user === null ||
    !('email' in payload.user) ||
    typeof payload.user.email !== 'string'
  ) {
    throw new Error('The session response did not match the closed web-session contract.');
  }
  return { user: { email: payload.user.email } };
}

export async function getWebSession() {
  return parseWebSession(await requestJson<unknown>('/api/auth/session'));
}

export async function signIn(email: string, password: string) {
  return parseWebSession(
    await requestJson<unknown>('/api/auth/sign-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }),
  );
}

export async function signOut() {
  await requestJson<unknown>('/api/auth/sign-out', { method: 'POST' });
}

export type IdempotencyHeaders = PathHeaders<'/v1/generations', 'post'>;
